import type {
  FieldErrors,
  ParsedBackendError,
  RawBreakdown,
} from './backend-error.model';

/**
 * Lee el cuerpo de un error del backend. TypeScript puro: CERO Angular.
 *
 * Valida FORMA, no contenido. Un cuerpo hostil o malformado puede hacer que la
 * escalera caiga al peldaño 2; no puede hacer que esto lance, y no puede
 * inyectar texto en la UI, porque quien pinta solo usa claves que el front posee.
 */

/** Llaves del sobre que descalifican a un cuerpo como mapa de errores por campo. */
const ENVELOPE_KEYS = ['error_code', 'message', 'detail', 'code', 'status'] as const;

/**
 * Un cuerpo es legible solo si es un objeto plano: ni `null`, ni string, ni
 * array, ni una instancia de `Error`.
 *
 * El caso `Error` no es paranoia: con `provideHttpClient(withFetch())` el
 * `FetchBackend` de Angular entrega un `SyntaxError` en `error` cuando el
 * cuerpo no era JSON (el `XhrBackend` deja el string). Ambas formas se toleran.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Error)
  );
}

function readString(body: Record<string, unknown>, key: string): string | null {
  const value = body[key];
  return typeof value === 'string' ? value : null;
}

/**
 * Forma admisible de un identificador de traza. Es el ÚNICO campo del cuerpo
 * que llega a la pantalla (`error-trace-id` lo pinta para que el usuario lo
 * dicte a soporte), así que no se acepta cualquier cadena: un `trace_id` de mil
 * caracteres, con saltos de línea o con prosa dentro, convertiría el campo en
 * una puerta para mostrar texto del backend — justo lo que este contrato vino a
 * cerrar. Angular lo interpola, así que no hay XSS; esto es contra la fuga y
 * contra el desborde, no contra la inyección.
 */
const TRACE_ID_ADMISIBLE = /^[A-Za-z0-9._:-]{1,64}$/;

/** Deja pasar el identificador solo si tiene forma de tal; si no, es como si no viniera. */
function sanearTraceId(value: string | null): string | null {
  return value !== null && TRACE_ID_ADMISIBLE.test(value) ? value : null;
}

function readTraceId(body: Record<string, unknown>, key: string): string | null {
  return sanearTraceId(readString(body, key));
}

/**
 * R41: `typeof === 'boolean'`, no `=== true`. La pregunta distingue las TRES
 * respuestas posibles del cuerpo —«sí», «no» y «no dije»— y `null` es la
 * tercera. Un `retryable: 'false'` (string) o `1` es un cuerpo que no habló el
 * contrato: cuenta como «no dije» y deja pasar el respaldo por status.
 *
 * **Decisión consciente, no accidente** (pineada por spec en
 * `backend-error.parser.spec.ts` y en `error-copy.service.spec.ts`): un
 * `retryable` presente pero NO booleano en un 5xx termina reportado como
 * REINTENTABLE, porque cae al respaldo por status. Se elige así porque el
 * contrato garantiza el tipo (§11.5: «un código nunca lleva datos»; el sobre es
 * generado, no redactado) y porque tratar «basura» como una declaración del
 * backend sería inventarle una intención que no tuvo.
 *
 * **Si el contrato algún día deja de garantizar el booleano, la lectura
 * conservadora en dinero es `false`**: ante un cuerpo que no se entiende, no
 * ofrecer «Reintentar» cuesta un ticket de soporte, y ofrecerlo puede costar una
 * operación duplicada. Ese día se cambia ESTA función (devolver `false` en vez
 * de `null` para el caso no booleano, dejando `null` solo para la llave
 * ausente) y el spec que lo pinea, no la escalera.
 */
function readRetryable(body: Record<string, unknown>): boolean | null {
  const value = body['retryable'];
  return typeof value === 'boolean' ? value : null;
}

/**
 * R12: el cuerpo es un mapa de errores por campo SOLO si no trae ninguna llave
 * del sobre y TODOS sus valores propios son arreglos de strings. Esa conjunción
 * es lo que mantiene separados el sobre y el mapa de campos.
 */
function readFieldErrors(body: Record<string, unknown>): FieldErrors | null {
  const entries = Object.entries(body);
  if (entries.length === 0) return null;
  if (ENVELOPE_KEYS.some(key => key in body)) return null;
  const isFieldMap = entries.every(
    ([, value]) => Array.isArray(value) && value.every(item => typeof item === 'string'),
  );
  if (!isFieldMap) return null;
  const result: Record<string, readonly string[]> = {};
  for (const [field, value] of entries) {
    if (Array.isArray(value)) {
      result[field] = value.filter((item): item is string => typeof item === 'string');
    }
  }
  return result;
}

export function parseBackendErrorBody(
  body: unknown,
  headerTraceId: string | null,
): ParsedBackendError {
  // La cabecera es tan del backend como el cuerpo: pasa por la misma criba.
  headerTraceId = sanearTraceId(headerTraceId);

  if (!isPlainObject(body)) {
    return {
      code: null,
      // Un cuerpo no parseable no declaró nada: `null`, no `false`. Es el caso
      // que el contrato llama «una respuesta que no pasó por el sobre», y el
      // respaldo por status es exactamente para él.
      retryable: null,
      traceId: headerTraceId,
      upstreamTraceId: null,
      classifyText: '',
      detailText: '',
      errorText: '',
      fieldErrors: null,
      breakdown: null,
    };
  }

  const rawDesglose = body['desglose'];
  const breakdown: RawBreakdown | null = isPlainObject(rawDesglose) ? rawDesglose : null;

  return {
    // R13/R14/R15: el código simbólico se lee SOLO de `error_code` y solo si es
    // string. `code` es el status HTTP repetido y `error` es prosa o detalle.
    code: readString(body, 'error_code'),
    // R41: el cuerpo puede decir «sí», «no» o nada. Las tres son distintas.
    retryable: readRetryable(body),
    // R7/R8: el cuerpo manda; la cabecera es el respaldo. Un `trace_id` con
    // forma inadmisible se descarta y cae a la cabecera, como si no viniera.
    traceId: readTraceId(body, 'trace_id') ?? headerTraceId,
    // R10: identificador distinto; jamás rellena `traceId`.
    upstreamTraceId: readTraceId(body, 'upstream_trace_id'),
    // R5: OPACO. Se lee para clasificar y no sale nunca hacia la UI.
    classifyText: readString(body, 'message') ?? readString(body, 'detail') ?? '',
    // R26: la heurística legacy los mira SEPARADOS; ver `backend-error.model.ts`.
    detailText: readString(body, 'detail') ?? '',
    errorText: readString(body, 'error') ?? '',
    fieldErrors: readFieldErrors(body),
    breakdown,
  };
}
